/* eslint-disable react/require-default-props */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaUpload, FaFileExcel, FaTimes, FaCheck } from 'react-icons/fa';

import Modal from '../../../../../components/Modal/Modal';
import { CtaButton, GhostButton } from '../../../../../styles/ui/Button';
import {
   FormGrid,
   Field,
   FieldLabel,
   FieldInput,
   FieldSelect,
   FieldError,
} from '../manual/styles';
import {
   TargetChip,
   UploadDesc,
   DropZone,
   FileChip,
} from './styles';
import {
   SCHEDULE_PERIOD_MORNING,
   SCHEDULE_PERIOD_AFTERNOON,
   formatWeekdayShortBr,
   formatDayMonthBr,
   formatSchedulePeriodLabel,
   formatFileSize,
   baseLabel,
} from './importHelpers';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_EXTENSIONS = '.xlsx';
const ACCEPTED_MIMETYPES =
   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

function validateFile(file) {
   if (!file) return 'Selecione um arquivo .xlsx do Refiltek.';
   if (!file.name.toLowerCase().endsWith('.xlsx')) {
      return 'Arquivo deve ser .xlsx (Excel).';
   }
   if (file.size > MAX_FILE_SIZE_BYTES) {
      return `Arquivo excede o limite de ${formatFileSize(MAX_FILE_SIZE_BYTES)}.`;
   }
   return null;
}

// ===========================================================================
// UploadModal — upload da planilha como MODAL (rework 0060). Prefilled com a
// data/período/base do `target` (estado compartilhado ou, no Slice 2, a célula
// do catálogo). "Analisar arquivo" é um POST read-only (preview, não persiste)
// → CtaButton escuro por FC7; só o Confirmar depois é azul.
// ===========================================================================
export default function UploadModal({
   isOpen,
   target,
   clusterOrigins = [],
   isAnalyzing = false,
   onClose,
   onAnalyze,
}) {
   const [scheduleDate, setScheduleDate] = useState('');
   const [schedulePeriod, setSchedulePeriod] = useState(SCHEDULE_PERIOD_MORNING);
   const [clusterOrigin, setClusterOrigin] = useState('');
   const [file, setFile] = useState(null);
   const [isDragging, setIsDragging] = useState(false);
   const [fileError, setFileError] = useState(null);
   const fileInputRef = useRef(null);

   // Cada abertura semeia do target (o modal não desmonta entre aberturas).
   useEffect(() => {
      if (!isOpen || !target) return;
      setScheduleDate(target.scheduleDate ?? '');
      setSchedulePeriod(target.schedulePeriod ?? SCHEDULE_PERIOD_MORNING);
      setClusterOrigin(target.clusterOrigin ?? '');
      setFile(null);
      setFileError(null);
      setIsDragging(false);
   }, [isOpen, target]);

   const baseOptions =
      clusterOrigins.length > 0
         ? clusterOrigins
         : [{ origin_code: clusterOrigin, origin_label: clusterOrigin }];

   const pickFile = (candidate) => {
      const error = validateFile(candidate);
      if (error) {
         setFile(null);
         setFileError(error);
         return;
      }
      setFile(candidate);
      setFileError(null);
   };

   const handleDrop = (event) => {
      event.preventDefault();
      setIsDragging(false);
      const dropped = event.dataTransfer.files?.[0];
      if (dropped) pickFile(dropped);
   };

   const handleInputChange = (event) => {
      const chosen = event.target.files?.[0];
      if (chosen) pickFile(chosen);
      // permite re-selecionar o mesmo arquivo após remoção
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
   };

   const handleAnalyze = () => {
      const error = validateFile(file);
      if (error) {
         setFileError(error);
         return;
      }
      onAnalyze({ file, scheduleDate, schedulePeriod, clusterOrigin });
   };

   const chip =
      `${formatWeekdayShortBr(scheduleDate)} ${formatDayMonthBr(scheduleDate)}` +
      ` · ${formatSchedulePeriodLabel(schedulePeriod)}` +
      ` · ${baseLabel(baseOptions, clusterOrigin)}`;

   const footer = (
      <>
         <GhostButton type="button" onClick={onClose} disabled={isAnalyzing}>
            Cancelar
         </GhostButton>
         <CtaButton
            type="button"
            onClick={handleAnalyze}
            disabled={!file || isAnalyzing}
         >
            <FaCheck size={15} />
            {isAnalyzing ? 'Analisando…' : 'Analisar arquivo'}
         </CtaButton>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title="Upload da planilha"
         maxWidth="540px"
         footer={footer}
      >
         <TargetChip>
            <span className="dotc" />
            {chip}
         </TargetChip>

         <UploadDesc>
            Confirme a data e o período do lote, depois envie o arquivo{' '}
            <b>.xlsx</b> do Refiltek. Os itens entram no rascunho desta
            roteirização.
         </UploadDesc>

         <FormGrid>
            <Field $span={6}>
               <FieldLabel htmlFor="upl-date">
                  Data agendada<span className="req">*</span>
               </FieldLabel>
               <FieldInput
                  id="upl-date"
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  disabled={isAnalyzing}
               />
            </Field>

            <Field $span={6}>
               <FieldLabel htmlFor="upl-period">
                  Período<span className="req">*</span>
               </FieldLabel>
               <FieldSelect
                  id="upl-period"
                  value={schedulePeriod}
                  onChange={(e) => setSchedulePeriod(e.target.value)}
                  disabled={isAnalyzing}
               >
                  <option value={SCHEDULE_PERIOD_MORNING}>Manhã</option>
                  <option value={SCHEDULE_PERIOD_AFTERNOON}>Tarde</option>
               </FieldSelect>
            </Field>

            <Field $span={12}>
               <FieldLabel htmlFor="upl-base">Base operacional</FieldLabel>
               <FieldSelect
                  id="upl-base"
                  value={clusterOrigin}
                  onChange={(e) => setClusterOrigin(e.target.value)}
                  disabled={isAnalyzing}
               >
                  {baseOptions.map((origin) => (
                     <option key={origin.origin_code} value={origin.origin_code}>
                        {origin.origin_label || origin.origin_code}
                     </option>
                  ))}
               </FieldSelect>
            </Field>
         </FormGrid>

         <DropZone
            $filled={Boolean(file)}
            $dragging={isDragging}
            role="button"
            tabIndex={0}
            onClick={() => !file && fileInputRef.current?.click()}
            onKeyDown={(e) => {
               if (!file && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  fileInputRef.current?.click();
               }
            }}
            onDragOver={(e) => {
               e.preventDefault();
               setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
         >
            <span className="drop-ic">
               <FaUpload size={22} />
            </span>
            {file ? (
               <>
                  <div className="drop-t">Arquivo pronto para análise</div>
                  <FileChip>
                     <FaFileExcel size={14} color="#1f8a5b" />
                     {file.name}
                     <span className="file-size" style={{ color: '#9aa1ab' }}>
                        ({formatFileSize(file.size)})
                     </span>
                     <button
                        type="button"
                        className="rm"
                        title="Remover"
                        onClick={(e) => {
                           e.stopPropagation();
                           setFile(null);
                           setFileError(null);
                        }}
                     >
                        <FaTimes size={13} />
                     </button>
                  </FileChip>
               </>
            ) : (
               <>
                  <div className="drop-t">Arraste a planilha .xlsx aqui</div>
                  <div className="drop-s">ou clique para selecionar o arquivo</div>
                  <div className="drop-h">Somente .xlsx — máximo 10 MB</div>
               </>
            )}
            <input
               ref={fileInputRef}
               type="file"
               accept={`${ACCEPTED_EXTENSIONS},${ACCEPTED_MIMETYPES}`}
               style={{ display: 'none' }}
               onChange={handleInputChange}
            />
         </DropZone>
         {fileError && (
            <FieldError style={{ marginTop: 8 }}>{fileError}</FieldError>
         )}
      </Modal>
   );
}

UploadModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   // { scheduleDate, schedulePeriod, clusterOrigin } — prefill do modal.
   // eslint-disable-next-line react/forbid-prop-types
   target: PropTypes.object,
   clusterOrigins: PropTypes.arrayOf(
      PropTypes.shape({
         origin_code: PropTypes.string,
         origin_label: PropTypes.string,
      }),
   ),
   isAnalyzing: PropTypes.bool,
   onClose: PropTypes.func.isRequired,
   onAnalyze: PropTypes.func.isRequired,
};
